import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Segment,
  Header,
  Dropdown,
  Button,
  Icon,
  Loader,
  Dimmer,
  Message
} from 'semantic-ui-react';
import StatCard from '../../../components/dashboard/StatCard';
import GaugeChart from '../../../components/dashboard/GaugeChart';
import DriversPerformanceTable from '../../../components/dashboard/DriversPerformanceTable';
import RankingTable from '../../../components/dashboard/RankingTable';
import PerformanceChart from '../../../components/dashboard/PerformanceChart';
import DriverDetailModal from '../../../components/dashboard/DriverDetailModal';
import { useRankingService } from '../../../services/backoffice/hooks/useRankingService';
import { ClientRanking, DriverRanking } from '../../../models/client-ranking';
import SessionStorage from '../../../store/session';
import useClientService from '../../../services/backoffice/hooks/useClientService';
import { ShortDto } from '../../../models/short-dto';
import { resolveDashboardPeriodRange } from '../../../shared/utils/dashboard-period';
import { rankingStateFromApiData } from '../../../shared/utils/dashboard-ranking-load';
import { isAxiosErrorAlreadyToasted } from '../../../services/axios-error-ui';
import './DashboardHome.css';

// Opções de período
const periodOptions = [
  { key: 'currentMonth', value: 'currentMonth', text: 'Mês atual' },
  { key: '7', value: '7', text: 'Últimos 7 dias' },
  { key: '15', value: '15', text: 'Últimos 15 dias' },
  { key: '30', value: '30', text: 'Últimos 30 dias' },
  { key: '60', value: '60', text: 'Últimos 60 dias' },
  { key: '90', value: '90', text: 'Últimos 90 dias' },
];

const DashboardHome: React.FC = () => {
  // Estados
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('30');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clients, setClients] = useState<{ key: string; value: string; text: string }[]>([]);
  const [rankingData, setRankingData] = useState<ClientRanking | null>(null);
  /** Intervalo do último load bem-sucedido — export Excel reutiliza para alinhar ao cache/tela. */
  const [loadedPeriodRange, setLoadedPeriodRange] = useState<{
    inicioIntervalo: string;
    fimIntervalo: string;
  } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState('');
  
  // Estados do modal de detalhes do motorista
  const [selectedDriver, setSelectedDriver] = useState<DriverRanking | null>(null);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);

  // Hooks
  const { getClientRanking, getUserClient, getDashboardExcel } = useRankingService();
  const clientService = useClientService();

  // Inicialização
  useEffect(() => {
    initializeDashboard();
  }, []);

  // Recarregar quando filtros mudam
  useEffect(() => {
    if (selectedClientId) {
      loadRankingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedClientId]);

  const initializeDashboard = async () => {
    try {
      // Buscar dados da sessão
      const session = await SessionStorage.getSession();
      const profile = session?.userData?.profile;
      const firstName = session?.userData?.firstName || 'Usuário';
      
      setUserName(firstName);
      setIsAdmin(profile === 'Admin');

      if (profile === 'Admin') {
        // Admin: carregar lista de clientes
        try {
          const clientList = await clientService.getAllClientsShortDto();
          const clientOptions = clientList.map(c => ({ 
            key: c.value || c.key, 
            value: c.value || c.key, 
            text: c.text || 'Cliente sem nome'
          }));
          setClients(clientOptions);
          
          // ID do cliente TNE TRANSPORTES (padrão para Admin)
          const TNE_TRANSPORTES_ID = '477eeb2d-81b4-4fb2-8f16-2f2350c0eebb';
          
          // Tentar encontrar TNE TRANSPORTES na lista
          const tneClient = clientOptions.find(c => c.value === TNE_TRANSPORTES_ID);
          
          if (tneClient) {
            // Se encontrou, selecionar TNE TRANSPORTES
            setSelectedClientId(tneClient.value);
          } else if (clientOptions.length > 0) {
            // Se não encontrou, usar o primeiro da lista como fallback
            setSelectedClientId(clientOptions[0].value);
          }
        } catch (err) {
          console.error('Erro ao carregar clientes:', err);
          // Fallback para mock se falhar
          setClients([
            { key: '1', value: '1', text: 'Transportadora ABC' },
          ]);
          setSelectedClientId('1');
        }
      } else {
        // Manager: buscar cliente automaticamente
        const userClient = await getUserClient();
        if (userClient?.clientId) {
          setSelectedClientId(userClient.clientId);
          setClients([{ 
            key: userClient.clientId, 
            value: userClient.clientId, 
            text: userClient.clientName || 'Meu Cliente' 
          }]);
        } else {
          setError('Você não está associado a nenhum cliente.');
        }
      }
    } catch (err) {
      setError('Erro ao inicializar dashboard');
      console.error(err);
    }
  };

  const loadRankingData = useCallback(async () => {
    if (!selectedClientId) return;
    
    setLoading(true);
    setError(null);

    try {
      const { inicioIntervalo, fimIntervalo } = resolveDashboardPeriodRange(period);
      const rangeIso = {
        inicioIntervalo: inicioIntervalo.toISOString(),
        fimIntervalo: fimIntervalo.toISOString(),
      };

      const data = await getClientRanking(selectedClientId, {
        ...rangeIso,
        topCount: 5
      });

      const { rankingData: nextRanking } = rankingStateFromApiData(data);
      setRankingData(nextRanking);
      setLoadedPeriodRange(nextRanking ? rangeIso : null);
    } catch (err: any) {
      console.error(err);
      setRankingData(null);
      setLoadedPeriodRange(null);
      // 403/5xx/rede: interceptor já toastou — Message full-page ainda dá "Tentar novamente".
      setError(err?.response?.data?.message || err?.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, period, getClientRanking]);

  // Funções do modal de detalhes do motorista
  const handleDriverClick = (driver: DriverRanking) => {
    setSelectedDriver(driver);
    setIsDriverModalOpen(true);
  };

  const handleCloseDriverModal = () => {
    setIsDriverModalOpen(false);
    setSelectedDriver(null);
  };

  const handleExportExcel = async () => {
    if (!selectedClientId || exporting) return;

    setExporting(true);
    try {
      // Reutiliza o intervalo do último load (mesma chave de cache 15min no servidor).
      // Fallback: só se ainda não houver load (edge) — recalcula now.
      const range =
        loadedPeriodRange ??
        (() => {
          const { inicioIntervalo, fimIntervalo } = resolveDashboardPeriodRange(period);
          return {
            inicioIntervalo: inicioIntervalo.toISOString(),
            fimIntervalo: fimIntervalo.toISOString(),
          };
        })();

      const blob = await getDashboardExcel(selectedClientId, range);

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const startDay = range.inicioIntervalo.slice(0, 10);
      const endDay = range.fimIntervalo.slice(0, 10);
      anchor.download = `dashboard-${selectedClientId}-${startDay}-${endDay}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Erro ao exportar dashboard:', err);
      // Evita Message + toast duplicados (interceptor já notifica 403/5xx/rede).
      if (!isAxiosErrorAlreadyToasted(err)) {
        setError(err?.message || 'Erro ao exportar dashboard para Excel');
      }
    } finally {
      setExporting(false);
    }
  };

  // Funções de formatação
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Render de erro
  if (error && !rankingData) {
    return (
      <div className="dashboard-home">
        <Message negative>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
          <Button primary onClick={initializeDashboard}>Tentar novamente</Button>
        </Message>
      </div>
    );
  }

  const totalPrizes = (rankingData?.summary.totalInertiaPrize || 0) + 
                      (rankingData?.summary.totalEngineLoadPrize || 0);

  return (
    <div className="dashboard-home">
      {error && rankingData && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-title">
          <Header as="h1">Dashboard de Performance</Header>
          <p>Acompanhe o desempenho dos motoristas da sua frota</p>
        </div>

        <div className="header-actions">
          <div className="filter-group">
            <span className="filter-label">Período:</span>
            <Dropdown
              selection
              options={periodOptions}
              value={period}
              onChange={(_, data) => setPeriod(data.value as string)}
              className="period-dropdown"
            />
          </div>

          {isAdmin && clients.length > 0 && (
            <div className="filter-group">
              <span className="filter-label">Cliente:</span>
              <Dropdown
                selection
                search
                options={clients}
                value={selectedClientId}
                onChange={(_, data) => setSelectedClientId(data.value as string)}
                className="client-dropdown"
                placeholder="Selecione um cliente"
              />
            </div>
          )}

          <Button
            className="export-btn"
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!selectedClientId || loading || exporting}
            title="Exportar dashboard para Excel"
          >
            <Icon name="download" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Segment className="dashboard-loading">
          <Dimmer active inverted>
            <Loader size="large">Carregando dashboard...</Loader>
          </Dimmer>
        </Segment>
      )}

      {/* Content */}
      {!loading && rankingData && (
        <>
          {/* Stats Cards */}
          <Grid columns={4} stackable className="stats-grid">
            <Grid.Column>
              <StatCard
                icon="users"
                iconColor="green"
                value={formatNumber(rankingData.summary.totalDrivers)}
                label="Motoristas Ativos"
                trend={{ value: '+3', positive: true }}
              />
            </Grid.Column>
            <Grid.Column>
              <StatCard
                icon="line graph"
                iconColor="blue"
                value={`${rankingData.summary.averageInertia.toFixed(1)}%`}
                label="Média de Inércia"
                trend={{ value: '+2.3%', positive: true }}
              />
            </Grid.Column>
            <Grid.Column>
              <StatCard
                icon="chart bar"
                iconColor="purple"
                value={`${rankingData.summary.averageMotorLoad.toFixed(1)}%`}
                label="Média Carga Motor"
                trend={{ value: '+1.8%', positive: true }}
              />
            </Grid.Column>
            <Grid.Column>
              <StatCard
                icon="money bill alternate"
                iconColor="orange"
                value={formatCurrency(totalPrizes)}
                label="Simulação de Prêmios"
                trend={{ value: '+R$ 420', positive: true }}
              />
            </Grid.Column>
          </Grid>

          {/* Charts Section */}
          <Grid columns={2} stackable className="charts-section">
            <Grid.Column>
              <Segment className="chart-card">
                <div className="chart-header">
                  <Header as="h3">Evolução de Performance</Header>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-dot blue"></span>
                      Carga Motor
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot green"></span>
                      Inércia
                    </div>
                  </div>
                </div>
                <PerformanceChart data={rankingData.evolution || []} />
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment className="chart-card">
                <div className="chart-header">
                  <Header as="h3">Médias Gerais</Header>
                </div>
                <div className="gauge-container">
                  <GaugeChart
                    value={rankingData.summary.averageMotorLoad}
                    label="Carga Motor (menor % = melhor)"
                    color="blue"
                  />
                  <GaugeChart
                    value={rankingData.summary.averageInertia}
                    label="Inércia (maior % = melhor)"
                    color="green"
                  />
                </div>
                <div className="km-summary">
                  <span className="km-label">Total KM rodados:</span>
                  <span className="km-value">{formatNumber(rankingData.summary.totalOdometro)} km</span>
                </div>
              </Segment>
            </Grid.Column>
          </Grid>

          {/* Ranking completo — carga em primeiro plano */}
          <Grid stackable className="rankings-section">
            <Grid.Column width={16}>
              <DriversPerformanceTable
                data={rankingData.drivers ?? []}
                onDriverClick={handleDriverClick}
              />
            </Grid.Column>
          </Grid>

          {/* Top 5 melhores/piores abaixo do ranking completo */}
          <Grid columns={2} stackable className="rankings-section top5-section">
            <Grid.Column>
              <RankingTable
                title="Top 5 Melhores"
                badge="Inércia"
                badgeType="best"
                metric="Maior %"
                data={rankingData.topDriversInertia ?? []}
                valueKey="inertiaPercentage"
                prizeKey="inertiaPrize"
                onDriverClick={handleDriverClick}
              />
            </Grid.Column>
            <Grid.Column>
              <RankingTable
                title="Top 5 Melhores"
                badge="Carga Motor"
                badgeType="best"
                metric="Menor %"
                data={rankingData.topDriversEngineLoad ?? []}
                valueKey="motorLoadPercentage"
                prizeKey="engineLoadPrize"
                onDriverClick={handleDriverClick}
              />
            </Grid.Column>
            <Grid.Column>
              <RankingTable
                title="Top 5 a Melhorar"
                badge="Inércia"
                badgeType="worst"
                metric="Menor %"
                data={rankingData.worstDriversInertia ?? []}
                valueKey="inertiaPercentage"
                prizeKey="inertiaPrize"
                onDriverClick={handleDriverClick}
              />
            </Grid.Column>
            <Grid.Column>
              <RankingTable
                title="Top 5 a Melhorar"
                badge="Carga Motor"
                badgeType="worst"
                metric="Maior %"
                data={rankingData.worstDriversEngineLoad ?? []}
                valueKey="motorLoadPercentage"
                prizeKey="engineLoadPrize"
                onDriverClick={handleDriverClick}
              />
            </Grid.Column>
          </Grid>
        </>
      )}

      {!loading && !rankingData && !error && (
        <Segment placeholder className="dashboard-empty">
          <Header icon>
            <Icon name="search" />
            Nenhum dado encontrado
          </Header>
          <p>Não há dados de performance para o período selecionado.</p>
        </Segment>
      )}

      <DriverDetailModal
        open={isDriverModalOpen}
        onClose={handleCloseDriverModal}
        driver={selectedDriver}
        period={period}
        clientAverage={rankingData ? {
          inertia: rankingData.summary.averageInertia,
          motorLoad: rankingData.summary.averageMotorLoad
        } : undefined}
      />
    </div>
  );
};

export default DashboardHome;
